// src/pages/Webhooks.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Copy,
  Link2,
  Shield,
  RefreshCw,
  Settings2,
  Send,
  CheckCircle2,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  ShieldAlert,
  X,
  Webhook,
} from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-sm shadow-[0_0_10px_rgba(56,189,248,0.05)]";

const HANDLER_URL =
  "https://whiteintel.io/api/regular/app/v2/webhook/get_webhook_handler.php";
const LICENSE_URL =
  "https://whiteintel.io/api/regular/app/v2/organizations/get_license.php";

function getJwtToken() {
  try {
    return (
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

/* ───────────────── Reusable UI: Dismissible Alerts ───────────────── */
function AlertBanner({ kind = "success", children, onClose }) {
  const isSuccess = kind === "success";
  const border = isSuccess ? "border-emerald-500/30" : "border-red-500/30";
  const bg = isSuccess ? "bg-emerald-500/10" : "bg-red-500/10";
  const text = isSuccess ? "text-emerald-200" : "text-red-200";
  const Icon = isSuccess ? CheckCircle2 : AlertTriangle;
  return (
    <div role="alert" className={`relative rounded-lg ${border} ${bg} px-3 py-2 text-sm ${text}`}>
      <div className="flex items-start gap-2 pr-6">
        <Icon className="h-4 w-4 mt-[2px]" />
        <div className="leading-relaxed">{children}</div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute right-1.5 top-1.5 rounded-md p-1 text-inherit hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ───────────────── Reusable: Copyable inline value ───────────────── */
function CopyInline({ children, copyText, className = "" }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <code className="font-mono break-all">{children}</code>
      <button
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(copyText ?? String(children ?? ""));
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          } catch {}
        }}
        className="rounded-md p-1 text-sky-300 hover:text-white hover:bg-white/10"
        title={copied ? "Copied" : "Copy"}
      >
        {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </span>
  );
}

/* ───────────────── Configure Modal ───────────────── */
function ConfigureWebhookModal({
  open,
  onClose,
  initialEndpoint,
  onSave,
  saving,
}) {
  const [endpoint, setEndpoint] = useState(initialEndpoint || "");

  useEffect(() => {
    if (open) {
      setEndpoint(initialEndpoint || "");
    }
  }, [open, initialEndpoint]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={`relative z-[101] w-full max-w-lg rounded-md ${frosted} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wh-title"
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-md bg-sky-500/15 border border-sky-600/30 flex items-center justify-center">
            <Settings2 className="h-4 w-4 text-sky-300" />
          </div>
          <h3 id="wh-title" className="text-base font-semibold text-white">
            Configure Webhook
          </h3>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-sm text-neutral-300 mb-1">Endpoint URL</label>
            <input
              type="url"
              placeholder="https://example.com/webhooks/whiteintel"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-sm outline-none"
            />
            <p className="mt-1 text-xs text-neutral-500">
              HTTPS endpoint that will receive POST requests from WhiteIntel.
            </p>
          </div>

          <div className="rounded-lg bg-neutral-900/50 border border-neutral-800 px-3 py-2 text-xs text-neutral-400">
            We sign each request with your secret. Rotate if you suspect exposure.
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5"
            disabled={saving}
          >
            Cancel
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onSave({ endpoint })}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/20 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/30 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:text-white hover:bg-white/10"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Confirm Rotate Secret Modal ───────────────── */
function ConfirmRotateSecretModal({ open, onClose, onConfirm, isLoading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={`relative z-[111] w-full max-w-md rounded-md ${frosted} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rotate-title"
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-md bg-amber-500/15 border border-amber-600/30 flex items-center justify-center">
            <RefreshCw className="h-4 w-4 text-amber-300" />
          </div>
          <h3 id="rotate-title" className="text-base font-semibold text-white">
            Rotate Signing Secret?
          </h3>
        </div>

        <div className="mt-3 text-sm text-neutral-300 space-y-2">
          <p>
            Rotating your webhook secret will <span className="font-semibold text-white">immediately change</span> the
            signature used for all future deliveries.
          </p>
          <div className="rounded-lg bg-neutral-900/50 border border-neutral-800 px-3 py-2 text-xs text-neutral-400">
            This may <span className="text-white">interrupt existing automations</span> until you update the secret in
            your downstream systems (e.g., environment variables, workflow tools, reverse proxies).
          </div>
          <p>Make sure you’ve updated your configurations before proceeding.</p>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-amber-600/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200 hover:bg-amber-500/20 disabled:opacity-60"
          >
            {isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
            Rotate Secret
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:text-white hover:bg-white/10"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Helpers ───────────────── */
function planAllowsWebhooks(org) {
  const plan = (org?.plan || org?.subscription || "").toString().toLowerCase();
  // Gate by TiFirm plan (case-insensitive, matches variants like "tifirm-pro")
  return plan.includes("tifirm");
}

/* ───────────────── Page ───────────────── */
export default function Webhooks() {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // subscription gate
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState(null);
  const [canUseWebhooks, setCanUseWebhooks] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);

  // configuration state from backend
  const [loading, setLoading] = useState(true);
  const [endpointUrl, setEndpointUrl] = useState("");
  const [secretToken, setSecretToken] = useState("");
  const [createdAt, setCreatedAt] = useState(null);
  const [isActive, setIsActive] = useState(false);

  // ui state
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [rotateModalOpen, setRotateModalOpen] = useState(false);

  // simulate
  const EVENT_TYPES = useMemo(
    () => [
      { id: "consumer", label: "consumer" },
      { id: "corporate", label: "corporate" },
      { id: "email", label: "email" },
      { id: "ip", label: "ip" },
      { id: "hostname", label: "hostname" },
    ],
    []
  );
  const [simType, setSimType] = useState("corporate");
  const [simSending, setSimSending] = useState(false);
  const [simResult, setSimResult] = useState(null); // { status, ms, body }

  // derived displays
  const secretMasked = useMemo(() => {
    if (!secretToken) return "—";
    if (showSecret) return secretToken;
    const last4 = secretToken.slice(-4);
    return `••••-••••-••••-${last4}`;
  }, [secretToken, showSecret]);

  /* ── 1) Load subscription, gate the page ── */
  useEffect(() => {
    (async () => {
      try {
        setSubLoading(true);
        setSubError(null);

        const res = await fetch(LICENSE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        const org = data?.organization || null;
        setOrgInfo(org);
        const allowed = planAllowsWebhooks(org);
        setCanUseWebhooks(allowed);
      } catch (e) {
        console.error(e);
        setSubError("Unable to verify your subscription.");
        setCanUseWebhooks(false);
      } finally {
        setSubLoading(false);
      }
    })();
  }, []);

  /* ── 2) Only after allowed => load webhook config ── */
  useEffect(() => {
    if (!canUseWebhooks) {
      setLoading(false); // avoid spinner if gated
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setApiError(null);
        setSuccess(null);

        const res = await fetch(HANDLER_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({ action: "get" }),
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || "Failed to load webhook settings.");
        }

        setEndpointUrl(data?.endpoint_url || "");
        setSecretToken(data?.secret_token || "");
        setCreatedAt(data?.created_at || null);
        setIsActive(Boolean(Number(data?.is_active || 0)));
      } catch (e) {
        console.error(e);
        setApiError(e.message || "Failed to load webhook configuration.");
      } finally {
        setLoading(false);
      }
    })();
  }, [canUseWebhooks]);

  /* ── Create / update endpoint ── */
  const handleSaveConfig = useCallback(
    async ({ endpoint }) => {
      try {
        setSaving(true);
        setApiError(null);
        setSuccess(null);

        const res = await fetch(HANDLER_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({ action: "create", endpoint_url: endpoint }),
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        if (!res.ok || data?.success !== true) {
          throw new Error(data?.error || "Save failed");
        }

        setEndpointUrl(endpoint || "");
        if (data?.secret) {
          setSecretToken(data.secret);
        }
        setSuccess("Webhook configuration saved.");
        setModalOpen(false);
      } catch (e) {
        console.error(e);
        setApiError(e.message || "Failed to save configuration.");
      } finally {
        setSaving(false);
      }
    },
    []
  );

  /* ── Rotate secret with confirmation ── */
  const handleRotateSecret = useCallback(async () => {
    try {
      setRotating(true);
      setApiError(null);
      setSuccess(null);

      const res = await fetch(HANDLER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ action: "regenerate" }),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const data = await res.json(); // {"success":true,"new_secret":"newSecret"}
      if (!res.ok || data?.success !== true || !data?.new_secret) {
        throw new Error(data?.error || "Rotate failed");
      }
      setSecretToken(data.new_secret);
      setSuccess("Signing secret rotated successfully.");
    } catch (e) {
      console.error(e);
      setApiError(e.message || "Failed to rotate secret.");
    } finally {
      setRotating(false);
      setRotateModalOpen(false);
    }
  }, []);

  /* ── Toggle active state ── */
  const handleToggleActive = useCallback(async () => {
    try {
      setToggling(true);
      setApiError(null);
      setSuccess(null);

      const action = isActive ? "deactivate" : "activate";
      const res = await fetch(HANDLER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ action }),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const data = await res.json();
      if (!res.ok || data?.success !== true) {
        throw new Error(data?.error || `Failed to ${action}`);
      }
      setIsActive(!isActive);
      setSuccess(`Webhook ${action === "activate" ? "enabled" : "disabled"}.`);
    } catch (e) {
      console.error(e);
      setApiError(e.message || "Failed to update status.");
    } finally {
      setToggling(false);
    }
  }, [isActive]);

  /* ── Simulate webhook via backend ── */
  const handleSimulate = useCallback(async () => {
    try {
      setSimSending(true);
      setApiError(null);
      setSuccess(null);
      setSimResult(null);

      const started = performance.now();
      const res = await fetch(HANDLER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ action: "test", type: simType }),
      });

      const ms = Math.max(1, Math.round(performance.now() - started));
      let text = "";
      try {
        text = await res.text();
      } catch {}
      setSimResult({ status: res.status, ms, body: text || "(no body)" });

      if (!res.ok) {
        try {
          const data = JSON.parse(text);
          throw new Error(data?.error || `Simulation failed with status ${res.status}.`);
        } catch {
          throw new Error(`Simulation failed with status ${res.status}.`);
        }
      }
      setSuccess("Webhook simulated successfully.");
    } catch (e) {
      console.error(e);
      setApiError(e.message || "Simulation failed.");
    } finally {
      setSimSending(false);
    }
  }, [simType]);

  /* ── CTA Handlers (when gated) ── */
  const handleUpgrade = () => {
    try {
      // Prefer in-app billing route if available:
      window.location.href = "/upgrades";
    } catch {
      window.open("mailto:sales@whiteintel.io", "_blank");
    }
  };

  const isGated = !subLoading && !canUseWebhooks;
  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Webhooks</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        <main className="relative flex-1 px-4 md:px-6">
          

          {isGated && (
            <div className="max-w-2xl mx-auto mt-12">
              <div className={`rounded-md ${frosted} p-8 text-center`}>
                <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
                  <Webhook className="h-6 w-6 text-sky-300" />
                </div>
                <h2 className="text-xl font-semibold text-white">
                  Webhooks are an upgraded feature
                </h2>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
                  Real-time event delivery and integration streaming are
                  available on the{" "}
                  <span className="text-sky-200 font-medium">
                    Threat Intelligence
                  </span>{" "}
                  plan.
                </p>
                <div className="mt-6 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleUpgrade}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
                  >
                    View upgrade options
                  </button>
                  <button
                    type="button"
                    onClick={() => { window.location.href = "/"; }}
                    className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
                  >
                    Back to dashboard
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Global alerts (dismissible) */}
          {!isGated && (
          <div className="mt-4 space-y-3">
            {success && (
              <AlertBanner kind="success" onClose={() => setSuccess(null)}>
                {success}
              </AlertBanner>
            )}
            {(apiError || subError) && (
              <AlertBanner kind="error" onClose={() => (subError ? setSubError(null) : setApiError(null))}>
                {subError || apiError}
              </AlertBanner>
            )}
          </div>
            )}

          {/* Content container (blur if gated and subscription loaded) */}
          {!isGated && (
          <div
            className={`mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 transition ${
              !subLoading && !canUseWebhooks ? "blur-sm pointer-events-none select-none" : ""
            }`}
            aria-hidden={!subLoading && !canUseWebhooks ? "true" : "false"}
          >
            {/* Card 1: Webhook Configuration */}
            <div className={`rounded-md ${frosted} p-5`}>
              <div className="flex items-center justify-between border-b border-sky-900/30 pb-3">
                <div className="text-lg font-semibold flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-sky-300" />
                  Webhook Configuration
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSuccess(null);
                      setApiError(null);
                      setModalOpen(true);
                    }}
                    disabled={!canUseWebhooks}
                    className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/10 px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
                  >
                    <Settings2 className="h-4 w-4" />
                    Configure
                  </button>

                  <button
                    onClick={handleToggleActive}
                    disabled={!endpointUrl || toggling || !canUseWebhooks}
                    className="inline-flex items-center gap-2 rounded-lg border border-neutral-700/60 bg-white/5 px-3 py-1.5 text-sm text-neutral-200 hover:bg-white/10 disabled:opacity-60"
                    title={isActive ? "Deactivate" : "Activate"}
                  >
                    {isActive ? (
                      <>
                        <ToggleRight className="h-4 w-4 text-emerald-300" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-4 w-4 text-neutral-400" />
                        Activate
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-4 text-sm text-neutral-300">
                {subLoading ? (
                  <div className="text-neutral-400">Checking subscription…</div>
                ) : loading ? (
                  <div className="text-neutral-400">Loading configuration…</div>
                ) : (
                  <>
                    <div className="flex justify-between border-b border-neutral-800 pb-2 mb-2">
                      <span className="text-neutral-400">Status</span>
                      <span className={`font-medium ${isActive ? "text-emerald-300" : "text-neutral-400"}`}>
                        {isActive ? "Enabled" : "Disabled"}
                      </span>
                    </div>

                    <div className="flex justify-between border-b border-neutral-800 pb-2 mb-2">
                      <span className="text-neutral-400">Endpoint</span>
                      <span className="text-right max-w-[70%]">
                        {endpointUrl ? (
                          <CopyInline copyText={endpointUrl}>{endpointUrl}</CopyInline>
                        ) : (
                          <span className="text-neutral-500">Not configured</span>
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between border-b border-neutral-800 pb-2 mb-2">
                      <span className="text-neutral-400">Created</span>
                      <span className="font-medium text-white">{createdAt || "—"}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-neutral-400">
                        <Shield className="h-4 w-4" />
                        <span>Signing Secret</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm break-all">{secretMasked}</span>
                        <button
                          onClick={() => setShowSecret((v) => !v)}
                          disabled={!canUseWebhooks}
                          className="rounded-md px-2 py-1 text-xs text-neutral-400 hover:text-white hover:bg-white/10 disabled:opacity-60"
                        >
                          {showSecret ? "Hide" : "Show"}
                        </button>
                        {secretToken && (
                          <button
                            onClick={() => {
                              setSuccess(null);
                              setApiError(null);
                              setRotateModalOpen(true);
                            }}
                            disabled={rotating || !canUseWebhooks}
                            className="inline-flex items-center gap-2 rounded-lg border border-amber-600/40 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-300 hover:bg-amber-500/20 disabled:opacity-60"
                            title="Rotate signing secret"
                          >
                            <RefreshCw className={`h-4 w-4 ${rotating ? "animate-spin" : ""}`} />
                            Rotate
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Card 2: Simulate Webhook */}
            <div className={`rounded-md ${frosted} p-5`}>
              <div className="flex items-center justify-between border-b border-sky-900/30 pb-3">
                <div className="text-lg font-semibold flex items-center gap-2">
                  <Send className="h-5 w-5 text-sky-300" />
                  Simulate Webhook
                </div>
                <div className="text-sm text-neutral-400">Sends a signed test event via backend</div>
              </div>

              <div className="mt-4 space-y-3 text-sm">
                <div>
                  <label className="mb-1 block text-neutral-300">Event Type</label>
                  <select
                    value={simType}
                    onChange={(e) => setSimType(e.target.value)}
                    disabled={!canUseWebhooks}
                    className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-sm outline-none disabled:opacity-60"
                  >
                    {EVENT_TYPES.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-neutral-500">The payload is generated on the server.</p>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={handleSimulate}
                    disabled={simSending || !canUseWebhooks}
                    className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/20 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/30 disabled:opacity-60"
                  >
                    {simSending ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" className="opacity-20" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path d="M4 12a8 8 0 018-8" className="opacity-80" stroke="currentColor" strokeWidth="4" fill="none" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send Test
                      </>
                    )}
                  </button>
                </div>

                {simResult && (
                  <div className="mt-3 rounded-md border border-neutral-800 bg-neutral-900/40 p-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-400">
                        Response: <span className="text-white font-medium">{simResult.status}</span>
                      </span>
                      <span className="text-neutral-400">
                        Time: <span className="text-white font-medium">{simResult.ms} ms</span>
                      </span>
                    </div>
                    <pre className="mt-2 text-xs whitespace-pre-wrap break-words text-neutral-300">
{simResult.body}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

        </main>
      </div>

      {/* Configure Modal */}
      <ConfigureWebhookModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialEndpoint={endpointUrl}
        onSave={handleSaveConfig}
        saving={saving}
      />

      {/* Rotate Secret Confirmation Modal */}
      <ConfirmRotateSecretModal
        open={rotateModalOpen}
        onClose={() => setRotateModalOpen(false)}
        isLoading={rotating}
        onConfirm={handleRotateSecret}
      />
    </div>
  );
}
